import { useSession, type Id } from '@/model';
import { lazy, Suspense } from 'react';
import { Skeleton } from '@/ui';

const ComposerView = lazy(() => import('./ComposerView').then(module => ({default:module.ComposerView})));

/** Frozen props (orchestrator). The chat screen mounts this under the transcript. */
export interface ComposerProps {
  conversationId: Id;
  /** Read-only chats (viewers, former members): shows the reason instead of an input. */
  disabled?: boolean;
  disabledReason?: string;
  /** Current, visible chat; default true. Inactive composers stop typing and private inference. */
  active?: boolean;
  /** Actual transcript follow state, independent of whether its window contains the latest message. */
  isFollowingLatest?: boolean;
  composeAccess?: 'compose' | 'postToJoin' | 'readOnly';
  /** Emitted once after this local gesture is durably queued, never for an acknowledgement/remote send. */
  onQueued?: (result: { clientId: Id }) => void;
  /** A changed integer focuses the enabled editor without changing its text. */
  focusRequest?: number;
}

/** Account and chat identity fence all local draft, media and capture work. */
export function Composer(props: ComposerProps) {
  const session = useSession();
  if (session.phase !== 'signedIn') return null;
  return <Suspense fallback={<Skeleton height={60}/>}><ComposerView key={`${session.accountId}:${props.conversationId}`} accountId={session.accountId} {...props} /></Suspense>;
}
